import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FaClock, FaPowerOff, FaBars, FaBell } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const AUTO_LOGOUT_TIME = 30 * 60 * 1000;

const API_BASE =
  (typeof window !== 'undefined' && window.__API_BASE__) ||
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE) ||
  '/Backend';

export default function Topbar({ user, theme = 'dark', onMenuToggle }) {
  const [time, setTime] = useState(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
  const [isScrolled, setIsScrolled] = useState(false);

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifMenu, setShowNotifMenu] = useState(false);

  const [timeright, setTimeright] = useState(AUTO_LOGOUT_TIME);
  const [showWarning, setShowWarning] = useState(false);

  const [notifs, setNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifs, setLoadingNotifs] = useState(false);

  const logoutTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const rootRef = useRef(null);

  const navigate = useNavigate();
  const userId = user?.id ?? null;

  const handleLogout = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login');
  }, [navigate]);

  const resetTimer = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    setTimeright(AUTO_LOGOUT_TIME);
    setShowWarning(false);

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      const startTime = Date.now();
      countdownIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 5 * 60 * 1000 - elapsed);
        setTimeright(remaining);
        if (remaining <= 0) {
          clearInterval(countdownIntervalRef.current);
          handleLogout();
        }
      }, 1000);

      logoutTimerRef.current = setTimeout(() => {
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        handleLogout();
      }, 5 * 60 * 1000);
    }, AUTO_LOGOUT_TIME - 5 * 60 * 1000);
  }, [handleLogout]);

  useEffect(() => {
    const clockTimer = setInterval(() => {
      setTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    }, 1000);
    resetTimer();

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    const resetTimerOnActivity = () => resetTimer();
    events.forEach((ev) => window.addEventListener(ev, resetTimerOnActivity));

    return () => {
      clearInterval(clockTimer);
      events.forEach((ev) => window.removeEventListener(ev, resetTimerOnActivity));
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [resetTimer]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setShowUserMenu(false);
        setShowNotifMenu(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const fetchNotifs = async () => {
    if (!userId) return;
    try {
      setLoadingNotifs(true);
      const res = await fetch(`${API_BASE}/notifications.php?mode=list&user_id=${encodeURIComponent(userId)}`, {
        credentials: 'include',
      });
      const data = await res.json();
      setNotifs(Array.isArray(data?.notifications) ? data.notifications : []);
      setUnreadCount(Number.isFinite(data?.unread) ? data.unread : 0);
    } catch (e) {
      console.error('Failed to load notifications', e);
    } finally {
      setLoadingNotifs(false);
    }
  };

  const markAllRead = async () => {
    if (!userId) return;
    try {
      await fetch(`${API_BASE}/notifications.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mode: 'mark_all_read', user_id: userId }),
      });
      setNotifs((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch (e) {
      console.error(e);
    }
  };

  const markOneRead = async (notificationId) => {
    if (!userId) return;
    try {
      await fetch(`${API_BASE}/notifications.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mode: 'mark_read', user_id: userId, notification_id: notificationId } ),
      });
      setNotifs((prev) => prev.map((n) => (n.id === notificationId ? { ...n, is_read: 1 } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!userId) return;
    fetchNotifs();
    const id = setInterval(fetchNotifs, 60_000);
    return () => clearInterval(id);
  }, [userId]);

  const profileImage = user?.image || '/default-user.png';
  const formatMs = (ms) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  };

  return (
    <>
      <header
  ref={rootRef}
  className="fixed top-0 left-0 right-0 z-20 backdrop-blur-md transition-colors duration-500"
  style={{
    height: 48,
    background: isScrolled 
      ? 'rgba(10, 19, 40, 0.8)'   // darker + transparent
      : 'rgba(11, 20, 44, 0.6)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  }}
>
 


        <div className="h-full px-3 md:px-4 flex items-center justify-between gap-2 text-white">
          {/* LEFT: hamburger only */}
          <div className="flex items-center gap-2">
            {onMenuToggle && (
              <button
                onClick={onMenuToggle}
                className="p-1.5 rounded-md bg-[#101a34] hover:bg-[#132043] transition"
                aria-label="Toggle menu"
              >
                <FaBars size={14} />
              </button>
            )}
          </div>

          {/* RIGHT cluster — minimal */}
          <div className="flex items-center gap-2">
            {/* time (hidden on very small screens) */}
            <div className="hidden xs:flex items-center gap-1.5 text-xs text-white/85">
              <FaClock className="opacity-85" size={12} />
              <span className="font-medium">{time}</span>
            </div>

            {/* notifications */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowNotifMenu((s) => !s);
                  setShowUserMenu(false);
                  fetchNotifs();
                }}
                className="p-1.5 rounded-md bg-[#101a34] hover:bg-[#132043] transition relative"
                aria-haspopup="menu"
                aria-expanded={showNotifMenu}
              >
                <FaBell size={14} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-[1rem] px-1 bg-rose-500 rounded-full flex items-center justify-center text-[9px] font-semibold">
                    {unreadCount}
                  </span>
                )}
              </button>

              {showNotifMenu && (
  <div
    role="menu"
    className="absolute right-0 mt-1 w-64 max-h-[60vh] overflow-hidden rounded-xl z-[60] shadow-2xl ring-1 ring-white/10"
    style={{
      background:
        'linear-gradient(180deg, rgba(13,22,45,0.98) 0%, rgba(9,15,33,0.98) 100%)',
      backdropFilter: 'none',            // stop header blur from affecting menu
      WebkitBackdropFilter: 'none',
      pointerEvents: 'auto',
      isolation: 'isolate'               // new stacking context for safety
    }}
  >
    <div className="flex items-center justify-between px-3 py-2 text-xs"
         style={{ background: 'rgba(14,23,48,0.98)' }}>
      <div className="font-semibold">Notifications</div>
      <button
        onClick={markAllRead}
        className="px-2 py-0.5 rounded bg-[#121a2b] hover:bg-[#162035] transition"
      >
        Mark all
      </button>
    </div>

    <div className="max-h-[48vh] overflow-auto bg-transparent">
      {loadingNotifs && <div className="p-3 text-xs text-white/70">Loading…</div>}
      {!loadingNotifs && notifs.length === 0 && (
        <div className="p-3 text-xs text-white/70">No notifications</div>
      )}
      {!loadingNotifs &&
        notifs.map((n) => (
          <div key={n.id} className="px-3 py-2 border-b border-white/5">
            <div className="flex items-start gap-2">
              <div className={`h-2 w-2 mt-1 rounded-full ${n.is_read ? 'bg-white/20' : 'bg-cyan-400'}`} />
              <div className="flex-1">
                <div className="text-xs font-medium">{n.title}</div>
                <div className="text-[11px] text-white/75 mt-0.5">{n.message}</div>
                <div className="text-[10px] text-white/60 mt-1">{n.created_at}</div>
                <div className="mt-1.5 flex gap-1.5">
                  {!n.is_read && (
                    <button
                      onClick={() => markOneRead(n.id)}
                      className="text-[10px] px-2 py-0.5 rounded bg-[#121a2b] hover:bg-[#162035] transition"
                    >
                      Read
                    </button>
                  )}
                  {n.link && (
                    <a
                      href={n.link}
                      className="text-[10px] px-2 py-0.5 rounded bg-[#121a2b] hover:bg-[#162035] transition"
                    >
                      Open
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
    </div>
  </div>
)}

            </div>

            {/* profile */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowUserMenu((s) => !s);
                  setShowNotifMenu(false);
                }}
                className="flex items-center gap-1.5 rounded-md bg-[#101a34] hover:bg-[#132043] p-1.5 transition"
                aria-haspopup="menu"
                aria-expanded={showUserMenu}
              >
                <div className="relative">
                  <img
                    src={profileImage}
                    alt="User"
                    className="h-7 w-7 rounded-md object-cover"
                    onError={(e) => (e.currentTarget.src = '/user.jpg')}
                  />
                  <span className="absolute -bottom-0.5 -right-0.5 block h-2 w-2 rounded-full bg-emerald-400 ring-2 ring-[#0b1120]" />
                </div>
                {/* hide name on small screens to keep it tiny */}
                <span className="hidden sm:inline text-xs font-medium">
                  {(user?.name || 'Leo Member').split(' ')[0]}
                </span>
              </button>

              {showUserMenu && (
  <div
    role="menu"
    className="absolute right-0 mt-1 w-56 rounded-xl overflow-hidden z-[60] shadow-2xl ring-1 ring-white/10"
    style={{
      background:
        'linear-gradient(180deg, rgba(13,22,45,0.98) 0%, rgba(9,15,33,0.98) 100%)',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      isolation: 'isolate'
    }}
  >
    <div className="px-3 py-2 text-xs"
         style={{ background: 'rgba(14,23,48,0.98)' }}>
      <div className="font-semibold truncate">{user?.name || 'Leo Member'}</div>
      <div className="text-white/70 truncate">{user?.email || ''}</div>
    </div>

    <div className="p-2 text-xs">
      <a href="/profile" className="block px-2 py-1.5 rounded hover:bg-[#121a2b] transition">
        Your Profile
      </a>
    </div>

    <div className="px-3 py-2 text-[11px] text-white/70">
      Session: {showWarning ? <span className="text-white/90 font-semibold">{formatMs(timeright)}</span> : 'Active'}
    </div>

    <button
      onClick={handleLogout}
      className="w-full flex items-center gap-2 px-3 py-2 text-red-300 hover:bg-[#121a2b] transition text-xs"
    >
      <FaPowerOff className="h-3.5 w-3.5" />
      Logout
    </button>
  </div>
)}

            </div>
          </div>
        </div>
      </header>

      {showWarning && (
        <div className="fixed top-[48px] right-0 z-40">
          <div
            className="mx-3 md:mx-4 p-1.5 rounded-md text-amber-200 text-[11px]"
            style={{ background: '#2a1e09' }}
          >
            Auto-logout in <span className="font-semibold">{formatMs(timeright)}</span>.
          </div>
        </div>
      )}

      {/* spacer below fixed bar */}
      <div style={{ height: 48 }} />
    </>
  );
}
